// ProjectTile.vue
<template>
  <div class="bg-white shadow-lg rounded-lg overflow-hidden">
    <!-- Project Header -->
    <div
      class="px-4 py-3 bg-keppel transition rounded-t-lg cursor-pointer group hover:bg-gray-800"
      @click="openJobDetail"
    >
      <div class="flex flex-col gap-3">
        <div class="w-full min-w-0 flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h2 class="text-lg font-semibold text-white transition group-hover:text-white truncate">
              <a
                :href="acceloLink('job', project.id)"
                target="_blank"
                rel="noopener noreferrer"
                class="hover:text-white focus:outline-none focus:underline group-hover:text-white"
                @click.stop
              >
                {{ project.title }}
              </a>
            </h2>
            <div class="mt-1 flex items-center gap-2 text-sm text-white transition group-hover:text-gray-100">
              <span>Status:</span>
              <StatusBadge
                :status="statusLabelForProject || project.standing"
                :label="statusLabelForProject || project.standing || 'Unknown'"
              />
            </div>
          </div>
          <div
            class="pm-card-toolbar"
            @click.stop
          >
            <button
              type="button"
              class="pm-card-toolbar__icon-button"
              :title="projectMilestonesExpanded ? 'Collapse all milestones' : 'Expand all milestones'"
              @click="toggleProjectExpansion"
            >
              <span class="pm-card-toolbar__chevron">⌃</span>
              <span class="pm-card-toolbar__chevron">⌄</span>
            </button>

            <div class="pm-filter">
              <button
                type="button"
                class="pm-filter__trigger"
                @click="statusMenuOpen = !statusMenuOpen"
              >
                Status: {{ statusFilterSummary }}
                <span class="pm-filter__caret">▾</span>
              </button>
              <div
                v-if="statusMenuOpen"
                class="pm-filter__menu pm-filter__menu--status"
              >
                <div class="pm-filter__section-title">Milestone</div>
                <label
                  v-for="option in milestoneStatusOptions"
                  :key="`milestone-${option.key}`"
                  class="pm-filter__option"
                >
                  <input
                    type="checkbox"
                    :checked="selectedMilestoneStatuses.includes(option.key)"
                    @change="toggleMilestoneStatus(option.key)"
                  >
                  <span>{{ option.label }}</span>
                </label>

                <div class="pm-filter__section-title pm-filter__section-title--divided">Task</div>
                <label
                  v-for="option in taskStatusOptions"
                  :key="`task-${option.key}`"
                  class="pm-filter__option"
                >
                  <input
                    type="checkbox"
                    :checked="selectedTaskStatuses.includes(option.key)"
                    @change="toggleTaskStatus(option.key)"
                  >
                  <span>{{ option.label }}</span>
                </label>
              </div>
            </div>

            <div class="pm-filter">
              <button
                type="button"
                class="pm-filter__trigger pm-filter__trigger--wide"
                @click="assigneeMenuOpen = !assigneeMenuOpen"
              >
                Milestone Manager/Task Assignee: {{ assigneeFilterSummary }}
                <span class="pm-filter__caret">▾</span>
              </button>
              <div
                v-if="assigneeMenuOpen"
                class="pm-filter__menu pm-filter__menu--assignee"
              >
                <input
                  v-model="assigneeSearch"
                  class="pm-filter__search"
                  type="text"
                  placeholder="Filter..."
                >
                <label
                  v-for="option in filteredAssigneeOptions"
                  :key="option.key"
                  class="pm-filter__option"
                >
                  <input
                    type="checkbox"
                    :checked="selectedAssignees.includes(option.key)"
                    @change="toggleAssignee(option.key)"
                  >
                  <span>{{ option.label }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col md:flex-row md:items-center md:gap-6">
          <div class="w-full md:w-1/2">
            <p class="text-[11px] tracking-wide text-white uppercase font-semibold transition group-hover:text-gray-100">
              Staff Assigned
            </p>
            <div class="mt-1 flex flex-wrap gap-2">
              <span
                v-if="!staffAssignedList.length"
                class="text-xs italic text-gray-200 transition group-hover:text-gray-100"
              >
                No assignees yet
              </span>
              <template v-else>
                <template
                  v-for="member in staffAssignedList"
                  :key="`${project.id}-${member.key}`"
                >
                  <RouterLink
                    v-if="member.id"
                    :to="{ name: 'EmployeeDashboard', params: { staffId: member.id } }"
                    class="px-2 py-0.5 rounded-full text-xs bg-white text-cerulean border border-white transition group-hover:bg-white group-hover:text-cerulean hover:bg-cerulean hover:text-white transform hover:scale-105 hover:shadow"
                    @click.stop
                  >
                    {{ member.name }}
                  </RouterLink>
                  <span
                    v-else
                    class="px-2 py-0.5 rounded-full text-xs bg-white text-cerulean border border-white transition group-hover:bg-white group-hover:text-cerulean transform hover:scale-105 hover:shadow"
                  >
                    {{ member.name }}
                  </span>
                </template>
              </template>
            </div>
          </div>

          <div class="w-full md:w-1/2">
            <p class="text-[11px] tracking-wide text-white uppercase font-semibold mb-1 transition group-hover:text-gray-100">
              Overall Progress
            </p>
            <div class="relative w-full h-4 bg-gray-200/70 rounded overflow-hidden shadow-inner">
              <div
                class="absolute top-0 left-0 h-4 bg-sushi"
                :style="{ width: overallProgress.usedPct + '%' }"
              />
              <div
                class="absolute top-0 right-0 h-4 bg-red-500"
                :style="{ width: overallProgress.overPct + '%' }"
              />
              <div
                class="absolute inset-0 flex items-center justify-center text-xs font-semibold text-white"
              >
                {{ formatDuration(totalLogged) }} / {{ formatDuration(totalBudget) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Grid Column Headings -->
    <div
      class="grid items-center bg-gray-100 text-sm text-gray-700 uppercase font-semibold px-4 py-2 border-t border-gray-200"
      style="grid-template-columns: 1.55fr 0.8fr 1.45fr 1.35fr 1.15fr 1.15fr;"
    >
      <div>Milestones & Tasks</div>
      <div>Status</div>
      <div>Time Allocated</div>
      <div class="pl-4">Usage / Budgeted</div>

      <!-- These two will occupy col 5 and col 6 via display: contents in the component -->
      <ScheduleFieldPicker :job-id="project.id" />
    </div>

    <!-- Milestones & their tasks -->
    <transition-group
      name="fade"
      tag="div"
    >
      <div
        v-for="ms in filteredMilestones"
        :key="ms.id"
      >
        <MilestoneRow
          :milestone="ms"
          :tasks="visibleMilestoneTasks(ms.id)"
          :expand-all="projectMilestonesExpanded"
        />
      </div>
    </transition-group>

    <!-- Project-level tasks (bottom, only if any) -->
    <div
      v-if="filteredProjectLevelTasks.length"
      :class="[
        'grid items-center px-2 py-1.5 border-t border-gray-200 cursor-pointer',
        { 'bg-keppel text-white': expandProjectTasks }
      ]"
      style="grid-template-columns: 1.55fr 0.8fr 1.45fr 1.35fr 1.15fr 1.15fr;"
      @click="toggleProjectTasks"
    >
      <div class="font-medium">
        Project Level Tasks
      </div>
      <div class="col-span-5" />
    </div>
    <transition name="fade">
      <div v-if="expandProjectTasks">
        <transition-group
          name="fade"
          tag="div"
        >
          <div
            v-for="task in filteredProjectLevelTasks"
            :key="task.id"
            class="grid items-center px-2 py-1.5 hover:bg-gray-50"
            style="grid-template-columns: 1.55fr 0.8fr 1.45fr 1.35fr 1.15fr 1.15fr;"
          >
            <div
              class="pr-2 min-w-0 flex flex-col gap-1"
              style="padding-left: 2em"
            >
              <div class="flex items-center gap-2">
                <span class="inline-block w-2 h-2 rounded-full bg-gray-400 flex-shrink-0" />
                <a
                  :href="acceloLink('task', task.id)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm font-semibold text-gray-800 truncate inline-block max-w-full hover:text-cerulean"
                >
                  {{ task.title }}
                </a>
              </div>
              <div
                v-if="taskAssigneeMember(task)"
                class="pl-4 flex flex-wrap items-center gap-2"
              >
                <RouterLink
                  v-if="taskAssigneeMember(task)?.id"
                  :to="taskAssigneeLink(task)"
                  class="px-2 py-0.5 rounded-full text-xs font-semibold bg-cerulean text-white border border-cerulean hover:bg-white hover:text-cerulean"
                  @click.stop
                >
                  {{ taskAssigneeMember(task)?.name }}
                </RouterLink>
                <span
                  v-else
                  class="px-2 py-0.5 rounded-full text-xs font-semibold bg-cerulean text-white border border-cerulean"
                >
                  {{ taskAssigneeMember(task)?.name }}
                </span>
              </div>
            </div>
            <div>
              <StatusBadge :status="task.standing" />
            </div>
            <div class="pr-3">
              <div class="relative w-full h-4 bg-gray-200 rounded overflow-hidden">
                <div
                  class="absolute top-0 left-0 h-4 rounded-l bg-sushi"
                  :style="{ width: taskProgress(task).usedPct + '%' }"
                />
                <div
                  class="absolute top-0 right-0 h-4 rounded-r bg-cerulean"
                  :style="{ width: taskProgress(task).remainingPct + '%' }"
                />
                <div
                  class="absolute top-0 right-0 h-4 rounded-r bg-red-500"
                  :style="{ width: taskProgress(task).overPct + '%' }"
                />
                <div
                  class="absolute inset-0 flex items-center justify-center text-xs font-medium text-white select-none"
                >
                  {{ formatUsagePercent(taskLogged(task), taskBudget(task)) }}
                </div>
              </div>
            </div>
            <div class="flex items-center gap-1 pl-4 text-sm font-medium text-slate-700 tabular-nums">
              <span
                class="inline-block text-right"
                style="width: 7ch;"
              >
                {{ formatDuration(taskLogged(task)) }}
              </span>
              <span>/</span>
              <span
                class="inline-flex items-center justify-center rounded-sm px-2 py-0.5 text-xs font-semibold leading-none text-white"
                :style="budgetBadgeStyle(taskLogged(task), taskBudget(task))"
              >
                {{ formatDuration(taskBudget(task)) }}
              </span>
            </div>
            <div class="text-sm font-medium text-slate-700 whitespace-nowrap">{{ formatDate(task.dateCommenced) }}</div>
            <div class="text-sm font-medium text-slate-700 whitespace-nowrap">{{ formatDate(task.dateDue) }}</div>
          </div>
        </transition-group>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { RouterLink } from 'vue-router'
import { useProjectManagementStore } from '@/pinia/projectManagementStore'
import MilestoneRow from './MilestoneRow.vue'
import ScheduleFieldPicker from './ScheduleFieldPicker.vue'
import StatusBadge from './StatusBadge.vue'

const STATUS_LABELS = {
  8: 'Paused',
  9: 'Complete',
  12: 'Cancelled',
  14: 'Active',
  15: 'Planned',
  16: 'Closing',
  17: 'Upgrade - Test Install',
  18: 'Upgrade - Prod Install',
  19: 'Upgrade QA',
  22: 'Design',
  23: 'Development',
  24: 'UAT',
  25: 'Production Support'
}

export default {
  name: 'ProjectTile',
  components: { MilestoneRow, ScheduleFieldPicker, StatusBadge, RouterLink },
  props: {
    project: { type: Object, required: true },
    expandAll: { type: Boolean, default: false }
  },
  setup(props) {
    const store = useProjectManagementStore()
    const router = useRouter()
    const expandProjectTasks = ref(props.expandAll)
    const projectMilestonesExpanded = ref(props.expandAll)
    const statusMenuOpen = ref(false)
    const assigneeMenuOpen = ref(false)
    const assigneeSearch = ref('')
    const selectedMilestoneStatuses = ref([])
    const selectedTaskStatuses = ref([])
    const selectedAssignees = ref([])
    const toggleProjectTasks = () => (expandProjectTasks.value = !expandProjectTasks.value)
    const toggleProjectExpansion = () => {
      projectMilestonesExpanded.value = !projectMilestonesExpanded.value
      expandProjectTasks.value = projectMilestonesExpanded.value
    }

    const normalizeFilterKey = (value) => {
      const normalized = String(value || '').trim().toLowerCase()
      return normalized || 'unassigned'
    }

    const labelFromKey = (key) =>
      String(key)
        .split(/[\s_-]+/)
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')

    const toggleFilterValue = (targetRef, value) => {
      targetRef.value = targetRef.value.includes(value)
        ? targetRef.value.filter(item => item !== value)
        : [...targetRef.value, value]
    }
    const toggleMilestoneStatus = (value) => toggleFilterValue(selectedMilestoneStatuses, value)
    const toggleTaskStatus = (value) => toggleFilterValue(selectedTaskStatuses, value)
    const toggleAssignee = (value) => toggleFilterValue(selectedAssignees, value)

    const milestoneStatusOptions = computed(() => {
      const statuses = new Set(['active', 'complete'])
      store.projectMilestones(props.project.id)
        .forEach(ms => statuses.add(normalizeFilterKey(ms.standing)))
      return [...statuses].map(key => ({ key, label: labelFromKey(key) }))
    })

    const milestoneTaskList = computed(() =>
      store.projectMilestones(props.project.id)
        .flatMap(ms => store.milestoneTasks(ms.id) || [])
    )

    const allProjectTasks = computed(() => [
      ...store.projectTasks(props.project.id),
      ...milestoneTaskList.value
    ])

    const taskStatusOptions = computed(() => {
      const statuses = new Set(['pending', 'started', 'complete'])
      allProjectTasks.value.forEach(task => statuses.add(normalizeFilterKey(task.standing)))
      return [...statuses].map(key => ({ key, label: labelFromKey(key) }))
    })

    const taskAssigneeKey = (task) => {
      const member = formatTaskAssignee(task?.assignee)
      if (!member) return 'unassigned'
      return String(member.id ?? member.name).toLowerCase()
    }

    const taskMatchesStatus = (task) =>
      !selectedTaskStatuses.value.length ||
      selectedTaskStatuses.value.includes(normalizeFilterKey(task.standing))

    const taskMatchesAssignee = (task) =>
      !selectedAssignees.value.length ||
      selectedAssignees.value.includes(taskAssigneeKey(task))

    const taskMatchesFilters = (task) =>
      taskMatchesStatus(task) && taskMatchesAssignee(task)

    const assigneeOptions = computed(() => {
      const seen = new Map()
      seen.set('unassigned', { key: 'unassigned', label: 'Unassigned' })
      allProjectTasks.value.forEach(task => {
        const member = formatTaskAssignee(task.assignee)
        if (!member) return
        const key = String(member.id ?? member.name).toLowerCase()
        if (!seen.has(key)) {
          seen.set(key, {
            key,
            label: member.name
          })
        }
      })
      return [...seen.values()]
    })

    const filteredAssigneeOptions = computed(() => {
      const query = assigneeSearch.value.trim().toLowerCase()
      if (!query) return assigneeOptions.value
      return assigneeOptions.value.filter(option =>
        option.label.toLowerCase().includes(query)
      )
    })

    const statusFilterSummary = computed(() => {
      if (!selectedMilestoneStatuses.value.length && !selectedTaskStatuses.value.length) return 'All'
      const total = selectedMilestoneStatuses.value.length + selectedTaskStatuses.value.length
      return `${total} selected`
    })

    const assigneeFilterSummary = computed(() =>
      selectedAssignees.value.length ? `${selectedAssignees.value.length} selected` : 'All'
    )

    const visibleMilestoneTasks = (milestoneId) =>
      (store.milestoneTasks(milestoneId) || []).filter(taskMatchesFilters)

    const hasTaskScopedFilters = computed(() =>
      selectedTaskStatuses.value.length > 0 || selectedAssignees.value.length > 0
    )

    const milestoneMatchesStatus = (milestone) =>
      !selectedMilestoneStatuses.value.length ||
      selectedMilestoneStatuses.value.includes(normalizeFilterKey(milestone.standing))

    const milestoneMatchesAssignee = (milestone) => {
      if (!selectedAssignees.value.length) return true
      return (store.milestoneTasks(milestone.id) || []).some(taskMatchesAssignee)
    }

    const filteredMilestones = computed(() =>
      store.projectMilestones(props.project.id)
        .filter(ms =>
          milestoneMatchesStatus(ms) &&
          milestoneMatchesAssignee(ms) &&
          (!hasTaskScopedFilters.value || visibleMilestoneTasks(ms.id).length > 0)
        )
    )

    const filteredProjectLevelTasks = computed(() =>
      store.projectTasks(props.project.id)
        .filter(task => !task.milestoneId && taskMatchesFilters(task))
    )

    const taskLogged = (task) => Number(task?.logged ?? task?.billable) || 0
    const taskBudget = (task) => Number(task?.objectBudget ?? task?.object_budget ?? task?.remaining) || 0

    const projectLoggedSubtotal = computed(() => Number(
      props.project?.loggedSubtotalSeconds ??
      props.project?.logged_subtotal ??
      props.project?.jobObjectBudget?.logged_subtotal ??
      props.project?.jobObjectBudget?.loggedSubtotal ??
      0
    ))

    const projectBudgetSubtotal = computed(() => Number(
      props.project?.serviceTimeSubtotalEstimateSeconds ??
      props.project?.service_time_subtotal_estimate ??
      props.project?.jobObjectBudget?.service_time_subtotal_estimate ??
      props.project?.jobObjectBudget?.serviceTimeSubtotalEstimate ??
      0
    ))

    const totalLogged = computed(() => {
      if (Number.isFinite(projectLoggedSubtotal.value) && projectLoggedSubtotal.value > 0) {
        return projectLoggedSubtotal.value
      }

      return allProjectTasks.value.reduce((sum, task) => sum + taskLogged(task), 0)
    })
    const totalBudget = computed(() => {
      if (Number.isFinite(projectBudgetSubtotal.value) && projectBudgetSubtotal.value > 0) {
        return projectBudgetSubtotal.value
      }

      return allProjectTasks.value.reduce((sum, task) => sum + taskBudget(task), 0)
    })
    const progressSegments = (loggedValue, budgetValue) => {
      const logged = Number(loggedValue) || 0
      const budget = Number(budgetValue) || 0
      if (logged <= 0 && budget <= 0) {
        return { usedPct: 0, remainingPct: 0, overPct: 0 }
      }
      if (logged > budget && logged > 0) {
        return {
          usedPct: Math.round((budget / logged) * 100),
          remainingPct: 0,
          overPct: Math.round(((logged - budget) / logged) * 100)
        }
      }
      if (budget <= 0) {
        return { usedPct: 0, remainingPct: 0, overPct: 0 }
      }
      const usedPct = Math.max(0, Math.min(100, Math.round((logged / budget) * 100)))
      return { usedPct, remainingPct: 100 - usedPct, overPct: 0 }
    }
    const overallProgress = computed(() => progressSegments(totalLogged.value, totalBudget.value))


    const formatTaskAssignee = (assignee) => {
      if (!assignee) return null
      if (typeof assignee === 'string') {
        const trimmed = assignee.trim()
        return trimmed || null
      }
      const idRaw = assignee.id ?? assignee.Id
      const id = Number(idRaw)
      const first = assignee.firstname || assignee.firstName || assignee.FirstName || ''
      const last = assignee.surname || assignee.lastName || assignee.LastName || ''
      const full = `${first} ${last}`.trim()
      const display = full || assignee.name || assignee.Name || assignee.email || assignee.Email
      return display ? { id: Number.isFinite(id) ? id : null, name: display } : null
    }

    const staffAssignedList = computed(() => {
      const seen = new Set()
      const members = []

      if (process.env.NODE_ENV !== 'production') {
        console.debug('[ProjectTile] assignees for project', props.project.id, allProjectTasks.value.map(t => ({
          id: t.id,
          assignee: t.assignee
        })))
      }

      for (const task of allProjectTasks.value) {
        const member = formatTaskAssignee(task.assignee)
        if (member) {
          const keyBase = member.id ?? member.name
          if (!keyBase) continue
          const key = String(keyBase).toLowerCase()
          if (!seen.has(key)) {
            seen.add(key)
            members.push({
              ...member,
              key: keyBase
            })
          }
        }
      }

      return members
    })

    const formatAssignee = (assignee) => formatTaskAssignee(assignee)?.name || null
    const taskAssigneeMember = (task) => formatTaskAssignee(task?.assignee) || null
    const taskAssigneeLink = (task) => {
      const member = taskAssigneeMember(task)
      if (!member?.id) return null
      return { name: 'EmployeeDashboard', params: { staffId: member.id } }
    }

    const taskProgress = (task) => progressSegments(taskLogged(task), taskBudget(task))

    const formatDuration = secs => {
      const h = Math.floor(secs / 3600)
      const m = Math.floor((secs % 3600) / 60)
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    }

    const formatUsagePercent = (loggedValue, budgetValue) => {
      const logged = Number(loggedValue) || 0
      const budget = Number(budgetValue) || 0
      if (budget <= 0) return '--'
      return `${Math.round((logged / budget) * 100)}%`
    }

    const budgetBadgeStyle = (loggedValue, budgetValue) => {
      const logged = Number(loggedValue) || 0
      const budget = Number(budgetValue) || 0
      return {
        backgroundColor: logged > budget ? '#f06464' : '#22a85a',
        minWidth: '6.5ch'
      }
    }

    const formatDate = epoch =>
      epoch ? new Date(epoch * 1000).toLocaleDateString() : '-'

    const acceloLink = (type, id) => `https://perbyte.accelo.com/?action=view_${type}&id=${id}`

    const openJobDetail = () => {
      router.push({ name: 'ProjectManagementJobDetail', params: { jobId: props.project.id } })
    }

    const normalizeStatusId = (raw) => {
      if (raw == null) return null
      if (typeof raw === 'object') {
        const value = raw.id ?? raw.Id ?? raw.statusId ?? raw.status_id
        const num = Number(value)
        return Number.isFinite(num) ? num : null
      }
      const num = Number(raw)
      return Number.isFinite(num) ? num : null
    }

    const statusIdForProject = computed(() =>
      normalizeStatusId(
        props.project.status ??
        props.project.statusId ??
        props.project.status_id ??
        props.project.standingId ??
        props.project.standing_id
      )
    )

    const statusLabelForProject = computed(() => {
      const id = statusIdForProject.value
      return id != null ? (STATUS_LABELS[id] || `Status ${id}`) : null
    })

    return {
      expandProjectTasks,
      toggleProjectTasks,
      projectMilestonesExpanded,
      toggleProjectExpansion,
      statusMenuOpen,
      assigneeMenuOpen,
      assigneeSearch,
      selectedMilestoneStatuses,
      selectedTaskStatuses,
      selectedAssignees,
      milestoneStatusOptions,
      taskStatusOptions,
      filteredAssigneeOptions,
      statusFilterSummary,
      assigneeFilterSummary,
      toggleMilestoneStatus,
      toggleTaskStatus,
      toggleAssignee,
      visibleMilestoneTasks,
      filteredMilestones,
      filteredProjectLevelTasks,
      staffAssignedList,
      taskAssigneeMember,
      taskAssigneeLink,
      taskLogged,
      taskBudget,
      formatAssignee,
      taskProgress,
      formatDuration,
      formatUsagePercent,
      formatDate,
      acceloLink,
      openJobDetail,
      statusLabelForProject,
      totalLogged,
      totalBudget,
      overallProgress,
      budgetBadgeStyle
    }
  }
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.pm-card-toolbar {
  position: relative;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-shrink: 0;
}

.pm-card-toolbar__icon-button {
  display: inline-flex;
  width: 2.25rem;
  height: 2.25rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: #fff;
  color: #4b5563;
  line-height: 0.75;
}

.pm-card-toolbar__icon-button:hover,
.pm-filter__trigger:hover {
  border-color: #9ca3af;
  background: #fff;
}

.pm-card-toolbar__chevron {
  display: block;
  height: 0.75rem;
  font-size: 0.875rem;
  line-height: 0.75rem;
}

.pm-filter {
  position: relative;
}

.pm-filter__trigger {
  display: inline-flex;
  height: 2.25rem;
  align-items: center;
  gap: 0.25rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: #fff;
  padding: 0 0.625rem;
  color: #4b5563;
  font-size: 0.875rem;
  white-space: nowrap;
}

.pm-filter__trigger--wide {
  min-width: 16rem;
}

.pm-filter__caret {
  color: #374151;
  font-size: 0.75rem;
}

.pm-filter__menu {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  z-index: 50;
  min-width: 15rem;
  border: 1px solid #d1d5db;
  background: #fff;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
}

.pm-filter__menu--assignee {
  min-width: 18rem;
  max-height: 22rem;
  overflow-y: auto;
  padding: 0.75rem;
}

.pm-filter__menu--status {
  padding: 0.75rem 0;
}

.pm-filter__section-title {
  padding: 0 0.75rem 0.5rem;
  color: #075985;
  font-size: 0.875rem;
  font-weight: 700;
}

.pm-filter__section-title--divided {
  margin-top: 0.625rem;
  padding-top: 0.625rem;
  border-top: 1px solid #e5e7eb;
}

.pm-filter__option {
  display: flex;
  min-height: 2rem;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  color: #1e3a8a;
  font-size: 0.875rem;
  cursor: pointer;
}

.pm-filter__option:hover {
  background: #f3f4f6;
}

.pm-filter__search {
  width: 100%;
  margin-bottom: 0.75rem;
  border: 1px solid #d1d5db;
  padding: 0.5rem;
  font-size: 0.875rem;
}

</style>
